import React from "react";
import styled, { keyframes, css } from "styled-components";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 200;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? "all" : "none")};
  transition: opacity 0.3s ease;
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 100vw;
  height: 100vh;
  background: #111;
  border-left: 1px solid #1e1e1e;
  z-index: 201;
  display: flex;
  flex-direction: column;
  animation: ${(p) =>
    p.$open
      ? css`
          ${slideIn} 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards
        `
      : css`
          ${slideOut} 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards
        `};
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px 28px 24px;
  border-bottom: 1px solid #1e1e1e;
`;

const DrawerTitle = styled.h2`
  color: #fff;
  font-size: 0.8rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 400;
  margin: 0;
`;

const CartCount = styled.span`
  color: #555;
  font-size: 0.75rem;
  letter-spacing: 0.1em;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #555;
  font-size: 1.4rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  transition: color 0.2s;
  font-family: "Georgia", serif;

  &:hover {
    color: #fff;
  }
`;

const ItemsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #222;
  }
`;

const CartItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px 28px;
  border-bottom: 1px solid #1a1a1a;
  transition: background 0.15s;

  &:hover {
    background: #161616;
  }
`;

const ItemImage = styled.div`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  background: #0a0a0a;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.p`
  color: #ddd;
  font-size: 0.88rem;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.p`
  color: #555;
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0 0 12px 0;
`;

const ItemBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid #2a2a2a;
`;

const QtyBtn = styled.button`
  background: none;
  border: none;
  color: #888;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    color 0.2s,
    background 0.2s;

  &:hover {
    color: #fff;
    background: #1e1e1e;
  }
`;

const QtyValue = styled.span`
  color: #ccc;
  font-size: 0.8rem;
  width: 28px;
  text-align: center;
`;

const ItemPrice = styled.span`
  color: #fff;
  font-size: 0.9rem;
  letter-spacing: 0.04em;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: "Georgia", serif;
  padding: 0;
  margin-left: 12px;
  transition: color 0.2s;

  &:hover {
    color: #c0392b;
  }
`;

const EmptyCart = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #333;
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  gap: 12px;
`;

const DrawerFooter = styled.div`
  padding: 24px 28px;
  border-top: 1px solid #1e1e1e;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TotalLabel = styled.span`
  color: #555;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const TotalValue = styled.span`
  color: #fff;
  font-size: 1.1rem;
  letter-spacing: 0.04em;
`;

const CheckoutBtn = styled.button`
  width: 100%;
  background: #fff;
  color: #0a0a0a;
  border: none;
  padding: 15px;
  font-size: 0.75rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: "Georgia", serif;
  transition: background 0.2s;

  &:hover {
    background: #ddd;
  }
`;

const ContinueBtn = styled.button`
  width: 100%;
  background: none;
  border: 1px solid #222;
  color: #555;
  padding: 12px;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  font-family: "Georgia", serif;
  margin-top: 10px;
  transition: all 0.2s;

  &:hover {
    color: #ccc;
    border-color: #444;
  }
`;

const CartDrawer = () => {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity,
    totalItems,
    totalPrice,
  } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    closeCart();
    navigate("/checkout");
  };

  return (
    <>
      <Overlay $open={isOpen} onClick={closeCart} />
      {isOpen && (
        <Drawer $open={isOpen}>
          <DrawerHeader>
            <DrawerTitle>
              Cart{" "}
              <CartCount>
                ({totalItems} {totalItems === 1 ? "item" : "items"})
              </CartCount>
            </DrawerTitle>
            <CloseBtn onClick={closeCart}>×</CloseBtn>
          </DrawerHeader>

          {items.length === 0 ? (
            <EmptyCart>
              <span style={{ fontSize: "2rem", opacity: 0.2 }}>∅</span>
              Your cart is empty
            </EmptyCart>
          ) : (
            <ItemsList>
              {items.map((item) => (
                <CartItem key={item.cartId}>
                  <ItemImage>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} />
                    ) : null}
                  </ItemImage>
                  <ItemInfo>
                    <ItemName>{item.name}</ItemName>
                    <ItemMeta>
                      {item.frameType} · {item.size}
                    </ItemMeta>
                    <ItemBottom>
                      <QuantityControl>
                        <QtyBtn
                          onClick={() =>
                            updateQuantity(item.cartId, item.quantity - 1)
                          }
                        >
                          −
                        </QtyBtn>
                        <QtyValue>{item.quantity}</QtyValue>
                        <QtyBtn
                          onClick={() =>
                            updateQuantity(item.cartId, item.quantity + 1)
                          }
                        >
                          +
                        </QtyBtn>
                      </QuantityControl>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <ItemPrice>
                          €{(item.price * item.quantity).toFixed(2)}
                        </ItemPrice>
                        <RemoveBtn onClick={() => removeItem(item.cartId)}>
                          remove
                        </RemoveBtn>
                      </div>
                    </ItemBottom>
                  </ItemInfo>
                </CartItem>
              ))}
            </ItemsList>
          )}

          {items.length > 0 && (
            <DrawerFooter>
              <TotalRow>
                <TotalLabel>Total</TotalLabel>
                <TotalValue>€{totalPrice.toFixed(2)}</TotalValue>
              </TotalRow>
              <CheckoutBtn onClick={handleCheckout}>
                Proceed to Checkout
              </CheckoutBtn>
              <ContinueBtn onClick={closeCart}>Continue Shopping</ContinueBtn>
            </DrawerFooter>
          )}
        </Drawer>
      )}
    </>
  );
};

export default CartDrawer;
